import { Card, useTheme } from "@mui/material";
import DescriptionIcon from '@mui/icons-material/Description';
import LoopIcon from '@mui/icons-material/Loop';
import { PharmaceuticalWasteItem, WasteStatus } from "../types";
import CompliancePlip from "../../../../components/CompliancePlip";
import { unusedMeds1 } from "../../../../icons";
import { standardDateFormat } from "../../../../helpers/date";
import { TableButton } from "../../../../components/Buttons.styled";
import { useState } from "react";
import axios from "axios";
import constants from "../../../../constants";
import ManifestModal from "../components/ManifestModal";
import ActiveInactiveModal from "../components/ActiveInactiveModal";
import { itemImages } from "../../../../helpers/image";
import { useAppDispatch } from "../../../../redux/store";
import { addError, addSuccess } from "../../../../redux/alerts";
import useCustomer from "../../../../hooks/useCustomer";

type Props = {
  item: PharmaceuticalWasteItem,
  getWasteData: () => void,
}

const WasteItem: React.FC<Props> = ({ item, getWasteData }) => {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const customer = useCustomer()
  const itemStatus = item.used_date === null ? 'inUse' : 'used';

  const [loadingItemId, setLoadingItemId] = useState<number | null>(null);
  const [openManifest, setOpenManifest] = useState<boolean>(false);
  const [openActiveUsed, setOpenActiveUsed] = useState<boolean>(false);

  let processor = {
    name: 'Reliable Pharmaceutical Returns',
    street: '1443 Donelson Pike',
    city: 'Nashville',
    state: 'TN',
    zip: '37217',
    facilityPermit: 'TRF190001532'
  };

  if (item?.vendor?.toLowerCase() === 'pristine') {
    processor = {
      name: 'Pristine Environmental',
      street: '3511 132nd St SW',
      city: 'Lynnwood',
      state: 'WA',
      zip: '98087',
      facilityPermit: 'PHWH.FX.60413153'
    }
  } else if (item?.vendor?.toLowerCase() === 'mws') {
    processor = {
      name: 'Medical Waste Services',
      street: '7321 Quimby Street',
      city: 'Paramount',
      state: 'CA',
      zip: '90723',
      facilityPermit: 'OST/TS #94',
    };
  }

  const getStatus = (status: number | undefined) => {
    if (status === WasteStatus.inUse) {
      return 'In Use';
    } else if (status === WasteStatus.used) {
      return 'Used';
    } else if (status === WasteStatus.destroyed) {
      return 'Processed';
    }
    return 'Unknown'
  }

  const getImage = (image: string | undefined) => {
    if (image) {
      const itemImage = itemImages[`${image}.png`];
      return itemImage
    }
    return unusedMeds1;
  }

  const handleMarkAsUsedActive = async (usedDate?: string | null) => {
    setOpenActiveUsed(false);

    try {
      const itemDetails = {
        customer_number: item.customer_number,
        used_date: item.disposal_status === WasteStatus.used ? null : usedDate ?? String(new Date())
      }

      setLoadingItemId(item.container_id)

      await axios.put(`${constants.ONTRAQ_API_URL}/api2/${item.customer_number}/pharmas/${item.container_id}`, { ...itemDetails })
      await getWasteData();

      dispatch(addSuccess(item.disposal_status === WasteStatus.used ? 'The item has been marked as In Use' : 'The item has been marked as Used'));
      setLoadingItemId(null);
    } catch (error) { 
      setLoadingItemId(null);
      console.error(error);
      dispatch(addError(error instanceof Error ? error.message : 'There was a problem updating the item'));
    }
  }

  const handleClose = () => {
    setOpenManifest(false);
  }

  const handleOpen = () => {
    setOpenManifest(true);
  }

  return (
    <Card sx={{ p: 2, mb: 2, color: theme.palette.color50555b.main, display: 'flex', flex: 'space-between' }} variant="outlined">
      <div className="flex flex-align-start flex-1">
        <img src={getImage(item?.item?.images)} alt="kit" style={{ width: '50px' }} className="m-r-12" />
        <div>
          <div className="flex flex-align-center">
            <CompliancePlip medium={true} status={item.status} message={item?.pharma_compliance?.compliance?.message_short} className="m-r-12" />
            <span className="f-s-17 m-r-8">{item?.item?.name ?? 'Pharmaceutical Waste Container'}</span>
            <LoopIcon sx={{ color: item?.isRenewal ? theme.palette.success.main : theme.palette.inactive.main }} fontSize="small" />
          </div>
          <div className="f-s-13">
            <div>Items #: {item.item_number}</div>
            <div>Product Serial #: {item.serial_number}</div>
            {item.used_date && <div>Used: {standardDateFormat(item.used_date)}</div>}
            {item.received_date && <div>Shipped: {standardDateFormat(item.received_date)}</div>}
            {item.received_date && <div>Received: {standardDateFormat(item.received_date)}</div>}
            {item.processed_date && <div>Processed: {standardDateFormat(item.processed_date)}</div>}
            <div>Status: {getStatus(item.disposal_status)}</div>
            {item.disposal_status === WasteStatus.destroyed && (
              <div className="flex link pointer" onClick={handleOpen}>
                <DescriptionIcon fontSize="small" />
                <div>Processing Certificate</div>
              </div>
            )}
          </div>
        </div>
      </div>
      {(item.disposal_status === WasteStatus.inUse || item.disposal_status === WasteStatus.used) && (
        <div>
          <TableButton variant="contained" onClick={() => setOpenActiveUsed(true)} disabled={loadingItemId === item.container_id}>
            Mark as {item.disposal_status === WasteStatus.inUse ? 'Used' : 'Active'}
          </TableButton>
        </div>
      )}
      <ManifestModal open={openManifest} handleClose={handleClose} category="pharmaceuticalWaste" customer={customer} item={item} processor={processor} />
      <ActiveInactiveModal itemStatus={itemStatus} open={openActiveUsed} handleClose={() => setOpenActiveUsed(false)} handleSubmit={handleMarkAsUsedActive} />
    </Card>
  )
}

export default WasteItem;